import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { Store } from '@ngrx/store';
import { SuiSidebarService, SuiTooltipDirective } from '@semeris/ui';
import { AppSelectors } from 'projects/semeris/src/store/app/app.selectors';
import {
  buildAllToolsCategories,
  hasToolPermission,
  isToolEnabledInWorkspace,
  MenuItem,
  resolvePinnedTools,
  SidebarGroup,
  toolNameForUrl,
  workspaceGateVerdict,
} from 'projects/semeris/src/store/menu/menu.selectors';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { RouterSelectors } from 'projects/semeris/src/store/router/router.selector';
import { UserProfileActions } from 'projects/semeris/src/store/user-profile/user-profile.actions';
import { UserProfileSelectors } from 'projects/semeris/src/store/user-profile/user-profile.selectors';

import { HELP_ID_DEFAULT } from '../../../help/models/help.model';
import { helpPageLink, isHelpPageUrl } from '../../../help/utils/help-page-url';
import { WsLinkPipe } from '../../../shared/pipes/ws-link.pipe';
import { PermissionService } from '../../../shared/services/permission.service';
import { isInWorkspaceAssetScope } from '../../../shared/utils/workspace-asset-scope';
import { WorkspaceRecordService } from '../../../workspace-ui/workspace-record.service';
import { WorkspaceSelectorComponent } from '../workspace-selector/workspace-selector.component';

const LOGO_ASSETS_BASE_URL = 'https://logo.assets.semeris.com/';

@Component({
  selector: 'semeris-app-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive, SuiTooltipDirective, WorkspaceSelectorComponent, WsLinkPipe],
  host: { class: 'flex flex-col min-h-0 flex-1' },
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {
  public readonly sidebarService = inject(SuiSidebarService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly permissionService = inject(PermissionService);

  private readonly allCategories = buildAllToolsCategories();
  private readonly pinnedToolsRaw = this.store.selectSignal(UserProfileSelectors.pinnedTools);
  private readonly userPermissions = toSignal(this.permissionService.permissions$, { initialValue: [] });
  /** Tools the active workspace exposes (derived from its legacy flag); undefined = no restriction (all tools). */
  private readonly workspaceEnabledTools = this.store.selectSignal(AppSelectors.selectedWorkspaceEnabledTools);
  private readonly pinnedToolsByWorkspace = this.store.selectSignal(UserProfileSelectors.pinnedToolsByWorkspace);
  private readonly selectedWorkspace = this.store.selectSignal(AppSelectors.selectedWorkspace);
  /** Asset classes the active workspace covers — hides tools whose data it holds none of. */
  private readonly workspaceAssets = this.store.selectSignal(AppSelectors.selectedWorkspaceAssetShortNames);
  private readonly workspaceRecord = inject(WorkspaceRecordService).active;

  /** Effective pins for the active workspace — the source both the sidebar and its pin toggle read. */
  private readonly pinnedTools = computed(() =>
    resolvePinnedTools(this.pinnedToolsByWorkspace(), this.pinnedToolsRaw(), this.selectedWorkspaceId(), this.selectedWorkspace()?.legacy),
  );

  /** Sidebar groups built dynamically from pinned tools, filtered by permissions. */
  public readonly menuGroups = computed<SidebarGroup[]>(() => {
    const pinned = new Set(this.pinnedTools());
    const perms = this.userPermissions();
    const enabledTools = this.workspaceEnabledTools();
    const record = this.workspaceRecord();
    const assets = this.workspaceAssets();

    const gatewayUnavailable = this.workspaceGatewayUnavailable();

    // New-style workspaces expose only their derived tool set; legacy (undefined) = all tools.
    // The sidebar is a working set, so a tool the workspace can't run is omitted rather than
    // shown disabled — All Tools is where it stays visible with a reason. Only `denied` hides one;
    // an outage leaves pins in place and the guard blocks the click.
    const hasWorkspaceAccess = (item: MenuItem): boolean =>
      isToolEnabledInWorkspace(item.name, enabledTools) &&
      isInWorkspaceAssetScope(item, assets) &&
      workspaceGateVerdict(item, record, gatewayUnavailable).status !== 'denied';

    return this.allCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) => pinned.has(item.name) && hasToolPermission(item, perms) && hasWorkspaceAccess(item)),
      }))
      .filter((group) => group.items.length > 0);
  });

  private readonly activeUrl = this.store.selectSignal(RouterSelectors.selectUrl);

  /** Name of the tool owning the current route — longest link wins, so `/documents/create` is `Submit Documents`, not `Uploaded docs`. */
  public readonly activeToolName = computed(() => toolNameForUrl(this.activeUrl() ?? ''));

  /** The tool owning the current route, or `null` when the sidebar already lists it / no tool owns the route. */
  public readonly activeUnlistedTool = computed<MenuItem | null>(() => {
    const name = this.activeToolName();
    if (!name || this.menuGroups().some((group) => group.items.some((item) => item.name === name))) {
      return null;
    }

    return this.allCategories.flatMap((category) => category.items).find((item) => item.name === name) ?? null;
  });

  public readonly isCollapsed = computed(() => this.sidebarService.mode() === 'collapsed');

  public readonly userGuideLink = helpPageLink(HELP_ID_DEFAULT);

  /** Every article, not just `main.html` — `routerLinkActive` would only match the one it links to. */
  public readonly onHelpPage = computed(() => isHelpPageUrl(this.activeUrl() ?? ''));

  private readonly profile = this.store.selectSignal(UserProfileSelectors.profile);

  /** Tenant logo (custom or the backend default), swapping to the small variant for the collapsed rail. */
  public readonly logoUrl = computed<string | null>(() => {
    const profile = this.profile();
    const path = this.isCollapsed() ? profile?.smallLogoImageURL : profile?.logoImageURL;

    return path ? `${LOGO_ASSETS_BASE_URL}${path}` : null;
  });

  /** Resets whenever the URL changes, so a missing small variant doesn't also hide a working large one. */
  private readonly logoFailed = linkedSignal<string | null, boolean>({ source: this.logoUrl, computation: () => false });

  /** Bundled Semeris logo once the profile is loaded but its logo is missing or failed to load (e.g. 404). */
  public readonly showFallbackLogo = computed(() => !!this.profile() && (!this.logoUrl() || this.logoFailed()));

  public readonly availableWorkspaces = this.store.selectSignal(AppSelectors.availableWorkspaces);

  public readonly selectedWorkspaceId = this.store.selectSignal(AppSelectors.selectedWorkspaceId);

  public readonly workspaceGatewayUnavailable = this.store.selectSignal(AppSelectors.workspaceGatewayUnavailable);

  public onWorkspaceSelected(workspaceId: string): void {
    this.store.dispatch(UserProfileActions.SetWorkspace({ workspaceId }));
  }

  public onLogoError(): void {
    this.logoFailed.set(true);
  }

  public isPinned(name: string): boolean {
    return this.pinnedTools().includes(name);
  }

  /** Pin/unpin from the footer's transient entry — same store write as the All Tools grid's star. */
  public togglePin(name: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const current = this.pinnedTools();
    const next = this.isPinned(name) ? current.filter((tool) => tool !== name) : [...current, name];
    this.store.dispatch(UserProfileActions.UpdatePinnedTools({ pinnedTools: next, workspaceId: this.selectedWorkspaceId() ?? null }));
  }
}
